import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// Define row-based layouts for supported languages, only letters
const LANGUAGE_KEYBOARDS = {
  es: [
    { keys: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], indent: 0 },
    { keys: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ñ'], indent: 1.5 },
    { keys: ['z', 'x', 'c', 'v', 'b', 'n', 'm'], indent: 3 }
  ],
  fr: [
    { keys: ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], indent: 0 },
    { keys: ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'], indent: 1.5 },
    { keys: ['w', 'x', 'c', 'v', 'b', 'n'], indent: 3 }
  ],
  de: [
    { keys: ['q', 'w', 'e', 'r', 't', 'z', 'u', 'i', 'o', 'p', 'ü'], indent: 0 },
    { keys: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä'], indent: 1.5 },
    { keys: ['y', 'x', 'c', 'v', 'b', 'n', 'm'], indent: 3 }
  ],
  it: [
    { keys: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], indent: 0 },
    { keys: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'], indent: 1.5 },
    { keys: ['z', 'x', 'c', 'v', 'b', 'n', 'm'], indent: 3 }
  ],
  pt: [
    { keys: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], indent: 0 },
    { keys: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ç'], indent: 1.5 },
    { keys: ['z', 'x', 'c', 'v', 'b', 'n', 'm'], indent: 3 }
  ],
  he: [
    { keys: ['ק', 'ר', 'א', 'ט', 'ו', 'ן', 'ם', 'פ'], indent: 0 },
    { keys: ['ש', 'ד', 'ג', 'כ', 'ע', 'י', 'ח', 'ל', 'ך', 'ף'], indent: 1.5 },
    { keys: ['ז', 'ס', 'ב', 'ה', 'נ', 'מ', 'צ', 'ת', 'ץ'], indent: 3 }
  ],
  yi: [
    { keys: ['ק', 'ר', 'א', 'ט', 'ו', 'ן', 'ם', 'פ'], indent: 0 },
    { keys: ['ש', 'ד', 'ג', 'כ', 'ע', 'י', 'ח', 'ל', 'ך', 'ף'], indent: 1.5 },
    { keys: ['ז', 'ס', 'ב', 'ה', 'נ', 'מ', 'צ', 'ת', 'ץ', 'װ', 'ױ', 'ײ'], indent: 3 }
  ],
  zh: [
    // Pinyin row
    { keys: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], indent: 0 },
    { keys: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'], indent: 1.5 },
    { keys: ['z', 'x', 'c', 'v', 'b', 'n', 'm'], indent: 3 }
  ],
  mandarin: null, // alias below
  'zh-CN': null, // alias below
  en: [
    { keys: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], indent: 0 },
    { keys: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'], indent: 1.5 },
    { keys: ['z', 'x', 'c', 'v', 'b', 'n', 'm'], indent: 3 }
  ], // English QWERTY
  ru: [
    { keys: ['й', 'ц', 'у', 'к', 'е', 'н', 'г', 'ш', 'щ', 'з', 'х', 'ъ'], indent: 0 },
    { keys: ['ф', 'ы', 'в', 'א', 'п', 'р', 'о', 'л', 'д', 'ж', 'э'], indent: 1.5 },
    { keys: ['я', 'ч', 'с', 'м', 'и', 'т', 'ь', 'б', 'ю'], indent: 3 }
  ],
  ar: [
    { keys: ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج', 'ד'], indent: 0 },
    { keys: ['ش', 'ס', 'ي', 'ב', 'ל', 'א', 'ת', 'נ', 'מ', 'כ', 'ט'], indent: 1.5 },
    { keys: ['ئ', 'ء', 'ؤ', 'ר', 'לא', 'ى', 'ة', 'ו', 'ז', 'ظ'], indent: 3 }
  ],
  ko: [
    { keys: ['ㅂ', 'ㅈ', 'ㄷ', 'ㄱ', 'ㅅ', 'ㅛ', 'ㅕ', 'ㅑ', 'ㅐ', 'ㅔ'], indent: 0 },
    { keys: ['ㅁ', 'ㄴ', 'ㅇ', 'ㄹ', 'ㅎ', 'ㅗ', 'ㅓ', 'ㅏ', 'ㅣ'], indent: 1.5 },
    { keys: ['ㅋ', 'ㅌ', 'ㅊ', 'ㅍ', 'ㅠ', 'ㅜ', 'ㅡ'], indent: 3 }
  ],
  ca: [
    { keys: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], indent: 0 },
    { keys: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ç'], indent: 1.5 },
    { keys: ['z', 'x', 'c', 'v', 'b', 'n', 'm'], indent: 3 },
    { keys: ['à', 'è', 'é', 'í', 'ï', 'ó', 'ò', 'ú', 'ü', '·'], indent: 0 }
  ],
  vi: [
    { keys: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], indent: 0 },
    { keys: ['a', 's', 'd', 'đ', 'f', 'g', 'h', 'j', 'k', 'l'], indent: 1.5 },
    { keys: ['z', 'x', 'c', 'v', 'b', 'n', 'm'], indent: 3 },
    { keys: ['ă', 'â', 'ê', 'ô', 'ơ', 'ư', 'à', 'á', 'ả', 'ã', 'ạ'], indent: 0 },
    { keys: ['è', 'é', 'ẻ', 'ẽ', 'ẹ', 'ì', 'í', 'ỉ', 'ĩ', 'ị'], indent: 0 },
    { keys: ['ò', 'ó', 'ỏ', 'õ', 'ọ', 'ù', 'ú', 'ủ', 'ũ', 'ụ', 'ỳ', 'ý'], indent: 0 }
  ],
  yue: [
    // Jyutping romanization (same Latin base as Mandarin pinyin keyboard)
    { keys: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], indent: 0 },
    { keys: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'], indent: 1.5 },
    { keys: ['z', 'x', 'c', 'v', 'b', 'n', 'm'], indent: 3 },
    { keys: ['1', '2', '3', '4', '5', '6'], indent: 0 }
  ],
};
LANGUAGE_KEYBOARDS.mandarin = LANGUAGE_KEYBOARDS.zh;
LANGUAGE_KEYBOARDS['zh-CN'] = LANGUAGE_KEYBOARDS.zh;
LANGUAGE_KEYBOARDS.cantonese = LANGUAGE_KEYBOARDS.yue;
LANGUAGE_KEYBOARDS['zh-hk'] = LANGUAGE_KEYBOARDS.yue;
LANGUAGE_KEYBOARDS['zh-yue'] = LANGUAGE_KEYBOARDS.yue;

// Fallback: show a basic Latin keyboard for unsupported languages
const DEFAULT_KEYS = [{ keys: ['a', 'e', 'i', 'o', 'u', 'n'], indent: 0 }];

const OnScreenKeyboard = ({
  languageCode = 'en',
  inputRef,
  onInsert,
  onClose,
}) => {
  const { t } = useLanguage();
  const layout = LANGUAGE_KEYBOARDS[languageCode] || DEFAULT_KEYS;

  // Insert character at cursor position
  const handleInsert = (char) => {
    if (onInsert) {
      onInsert(char);
    } else if (inputRef && inputRef.current) {
      const input = inputRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const value = input.value;
      const newValue = value.slice(0, start) + char + value.slice(end);

      // Update the input value
      input.value = newValue;

      // Set cursor position after the inserted character
      input.setSelectionRange(start + char.length, start + char.length);

      // Ensure the input maintains focus
      input.focus();

      // Trigger onChange event for controlled components
      const inputEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(inputEvent);

      // Also trigger a change event to ensure React state updates
      const changeEvent = new Event('change', { bubbles: true });
      input.dispatchEvent(changeEvent);
    }
  };

  // If no keys, don't render
  if (!layout.length || (layout.length === 1 && layout[0].keys.length === 0)) return null;

  return (
    <div className="on-screen-keyboard-overlay">
      <div className="on-screen-keyboard" style={{ position: 'relative' }}>
        {/* X close button */}
        {onClose && (
          <button
            type="button"
            aria-label={t('closeKeyboard')}
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '0.2rem',
              right: '0.2rem',
              background: 'none',
              border: 'none',
              color: '#888',
              fontSize: '1.3rem',
              cursor: 'pointer',
              zIndex: 10,
              padding: 0,
              width: '2rem',
              height: '2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="4" x2="16" y2="16" />
              <line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          </button>
        )}
        {layout.map((row, rowIdx) => (
          <div
            className="keyboard-row"
            key={rowIdx}
          >
            {row.keys.map((key, idx) => (
              <button
                key={idx}
                type="button"
                className="keyboard-key"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleInsert(key);
                }}
              >
                {key}
              </button>
            ))}
          </div>
        ))}
        {/* Space bar row */}
        <div className="keyboard-row" style={{ display: 'flex', justifyContent: 'center', marginTop: '0.3rem' }}>
          <button
            type="button"
            className="keyboard-key space-bar-key"
            style={{ minWidth: '8rem', fontSize: '1.1rem' }}
            onMouseDown={(e) => {
              e.preventDefault();
              handleInsert(' ');
            }}
          >
            {t('space')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnScreenKeyboard;